import React, {
  Children,
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import styled from "@emotion/styled";
import { zIndex, dataVizLayout } from "@atlas-int/tokens";
import { CloseButton as BaseCloseButton } from "@atlas-int/ui";

import {
  TooltipPosition,
  TooltipTheme,
  TooltipBase,
  ArrowContainer,
  Arrow,
  farEndOfScreenToggleClass,
  arrowContainerClassName,
  flipArrowClassName,
  leftArrowClassName,
  rightArrowClassName,
} from "./tooltip/SharedTooltipComponents";

// ── Styled (hoisted, stable identity) ────────────────────────────────

const CloseButtonStyled = styled(BaseCloseButton)`
  position: absolute;
  top: ${dataVizLayout.tooltipCloseInset}px;
  right: ${dataVizLayout.tooltipCloseInset}px;
  opacity: 0.7;
  z-index: ${zIndex.base};
  padding: 0;
  &:hover {
    opacity: 1;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
`;

// ── Types ────────────────────────────────────────────────────────────

export interface TooltipPortalProps {
  /** Content to display. Pass null/undefined to hide the tooltip entirely. */
  explanation: React.ReactNode | null;
  /** Viewport-relative { top, left } coordinates to position the tooltip at. */
  coords: { top: number; left: number };
  /** Whether the tooltip is pinned (enables close button + click-outside dismiss). */
  isFixed?: boolean;
  theme?: TooltipTheme;
  tooltipPosition?: TooltipPosition;
  overrideStyles?: boolean;
  /** When true, tooltip width sizes to content instead of using a fixed baseline width. */
  flexWidth?: boolean;
  /** Snap to a data-point position instead of following the cursor. */
  snapToPosition?: boolean;
  /**
   * Direction to snap the tooltip relative to the data point.
   * - `"above"`: center above the point (e.g. time-series charts)
   * - `"right"`: to the right of the point, flipping left if near viewport edge (default when snapping)
   */
  snapDirection?: "above" | "right";
  /** Called when a fixed tooltip is dismissed (close button or click-outside). */
  onClose?: () => void;
  /** When true, clicking outside the tooltip does not dismiss it. Only the close button will. Defaults to false. */
  disableClickOutsideDismiss?: boolean;
  /**
   * Override the tooltip's CSS z-index. Defaults to `zIndex.overlay` (3000).
   * Use a lower value (e.g. `zIndex.tooltip`) to keep the tooltip below dropdowns.
   */
  zIndex?: number;
  /**
   * DOM element ID for the portal container.
   * Falls back to rendering into `document.body` when omitted or when the element is not found.
   */
  portalContainerId?: string;
}

// ── Component ────────────────────────────────────────────────────────

/**
 * Renders tooltip content in a portal positioned at the given coordinates.
 *
 * **Pure renderer** — does not manage hover state, mouse tracking, or
 * children wrapping.  Callers decide _when_ and _where_ to show the tooltip.
 *
 * - For inline label tooltips → {@link TooltipTrigger}
 * - For chart tooltips with isolation → {@link TooltipStateBridge}
 * - For direct control (e.g. Overtime's snap behavior) → use this directly
 */
const TooltipPortal = memo(function TooltipPortal({
  explanation,
  coords,
  isFixed = false,
  theme,
  tooltipPosition,
  overrideStyles,
  flexWidth,
  snapToPosition,
  snapDirection = "right",
  onClose,
  zIndex: zIndexProp,
  portalContainerId,
  disableClickOutsideDismiss = false,
}: TooltipPortalProps) {
  const tooltipEl = useRef<HTMLDivElement | null>(null);
  const portalContainerRef = useRef<HTMLElement | null>(null);

  // Resolve portal container once on mount (avoids querySelector every render)
  useLayoutEffect(() => {
    portalContainerRef.current = portalContainerId
      ? document.querySelector<HTMLElement>(`#${portalContainerId}`)
      : null;
  }, [portalContainerId]);

  // Click-outside dismissal for fixed tooltips
  useEffect(() => {
    if (!isFixed || !onClose || disableClickOutsideDismiss) return;
    const handleGlobalClick = (e: MouseEvent) => {
      if (tooltipEl.current && !tooltipEl.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("click", handleGlobalClick);
    return () => document.removeEventListener("click", handleGlobalClick);
  }, [isFixed, onClose, disableClickOutsideDismiss]);

  // Position via direct DOM manipulation — avoids reflowing the React tree.
  useLayoutEffect(() => {
    const tooltipElm = tooltipEl.current;
    if (!explanation || !tooltipElm) return;

    if (Children.count(explanation) === 0) {
      tooltipElm.style.cssText = "opacity: 0;";
      return;
    }

    const { top, left } = coords;
    const sp = dataVizLayout.tooltipOffset;
    const h = tooltipElm.offsetHeight;
    const w = tooltipElm.offsetWidth;
    let y: number;
    let x: number;
    let isSnappedLeftRight = false;

    if (snapToPosition) {
      if (snapDirection === "above") {
        // Center above the data point
        y = top - sp / 2 - h;
        x = left - w / 2;
        tooltipElm.classList.remove(leftArrowClassName, rightArrowClassName);
      } else {
        isSnappedLeftRight = true;
        // To the right of the data point
        y = top - h / 2;
        x = left + sp;
        if (x + w + sp > window.innerWidth) {
          x = left - w - sp;
          tooltipElm.classList.add(rightArrowClassName);
          tooltipElm.classList.remove(leftArrowClassName);
        } else {
          tooltipElm.classList.add(leftArrowClassName);
          tooltipElm.classList.remove(rightArrowClassName);
        }
      }
    } else {
      // Default: above the cursor
      y = top - sp - h;
      x = left - w / 2;
      tooltipElm.classList.remove(leftArrowClassName, rightArrowClassName);
    }

    // Flip arrow if above viewport
    if (y < 0 || tooltipPosition === TooltipPosition.Bottom) {
      y = top + sp * 2;
      tooltipElm.classList.add(flipArrowClassName);
    } else {
      tooltipElm.classList.remove(flipArrowClassName);
    }

    // Remember ideal x before clamping to compute arrow offset
    const idealX = x;

    // Clamp to viewport edges
    x = Math.max(x, sp);
    if (x + w + sp > window.innerWidth) {
      x = window.innerWidth - w - sp;
    }

    tooltipElm.classList.remove(farEndOfScreenToggleClass);

    // Offset the arrow so it still points at the cursor/data point after clamping
    const arrowContainer = tooltipElm.querySelector<HTMLElement>(
      `.${arrowContainerClassName}`,
    );
    if (arrowContainer && !isSnappedLeftRight) {
      const shift = idealX - x;
      if (Math.abs(shift) > 1) {
        // Arrow needs to shift to stay aligned with the target
        arrowContainer.style.cssText = `justify-content:flex-start;padding-left:${Math.max(sp, left - x - 8)}px;`;
      } else {
        arrowContainer.style.cssText = "";
      }
    } else if (arrowContainer) {
      arrowContainer.style.cssText = "";
    }

    tooltipElm.style.cssText = `left:${x}px;top:${y}px;opacity:1;`;
  }, [
    explanation,
    coords,
    tooltipPosition,
    isFixed,
    snapToPosition,
    snapDirection,
  ]);

  // ── Render ───────────────────────────────────────────────────────

  const portalContainer = portalContainerRef.current ?? document.body;
  if (!explanation) return null;

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose?.();
  };

  return ReactDOM.createPortal(
    <TooltipBase
      ref={tooltipEl}
      $theme={theme}
      $overrideStyles={overrideStyles}
      $flexWidth={flexWidth}
      $isFixed={isFixed}
      $zIndex={zIndexProp}
      className={
        tooltipPosition === TooltipPosition.Right
          ? leftArrowClassName
          : undefined
      }
    >
      <ContentWrapper>
        {isFixed && <CloseButtonStyled size="sm" onClick={handleCloseClick} />}
        {explanation}
      </ContentWrapper>
      <ArrowContainer
        $position={tooltipPosition}
        className={arrowContainerClassName}
      >
        <Arrow $theme={theme} $position={tooltipPosition} />
      </ArrowContainer>
    </TooltipBase>,
    portalContainer,
  );
});

export default TooltipPortal;
